/**
 * Prefetch de datos para páginas de estadísticas (plantilla carga+caché).
 * Se invoca al expandir la sección en el Sidebar para que al entrar a la página los datos ya estén en caché.
 */

import { supabase } from '@/lib/supabase';
import {
  setStatsPageCache,
  codAnalyticsCacheKey,
  loyaltyDashboardCacheKey,
  statisticsCacheKey,
  customersCacheKey,
  productsCacheKey,
  listViewCacheKey,
} from '@/lib/statsPageCache';
import { slugifyProductName } from '@/config/routes.config';
import { initializeDeliveryMethods } from '@/lib/ecommerce-init';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import { toTitleCase } from '@/lib/stringFormat';

export interface CodAnalyticsPayload {
  totalOrders: number;
  totalRevenue: number;
}

export async function fetchCodAnalyticsData(tenantId: string): Promise<CodAnalyticsPayload> {
  // Dominio único orders_v2: pedidos de formulario COD = channel 'cod_form'; total = grand_total.
  const { data, error } = await supabase
    .from('orders_v2')
    .select('id, grand_total')
    .eq('tenant_id', tenantId)
    .eq('channel', 'cod_form');
  if (error) throw error;
  const list = data ?? [];
  return {
    totalOrders: list.length,
    totalRevenue: list.reduce((s, o) => s + Number(o.grand_total ?? 0), 0),
  };
}

export async function prefetchCodAnalyticsStats(tenantId: string): Promise<void> {
  try {
    const payload = await fetchCodAnalyticsData(tenantId);
    setStatsPageCache(codAnalyticsCacheKey(tenantId), payload);
  } catch {
    // Prefetch en segundo plano: no mostrar error
  }
}

// --- Loyalty Dashboard ---

export interface LoyaltyDashboardPayload {
  totalMembers: number;
  activeMembers: number;
  totalPointsIssued: number;
  totalPointsRedeemed: number;
  totalRewardsRedeemed: number;
  averagePointsPerCustomer: number;
  topTier: string;
  membersByTier: {
    basic?: number;
    premium?: number;
    bronze: number;
    silver: number;
    gold: number;
    platinum?: number;
  };
}

export async function fetchLoyaltyDashboardData(tenantId: string): Promise<LoyaltyDashboardPayload> {
  const { data: wallets, error: walletsError } = await supabase
    .from('wallets')
    .select('id, loyalty_points, tier, total_spent, last_transaction_at')
    .eq('tenant_id', tenantId);
  if (walletsError) throw walletsError;

  const walletIds = (wallets ?? []).map((w) => w.id);
  let transactions: { points_earned?: number; transaction_type?: string; wallet_id: string }[] = [];
  if (walletIds.length > 0) {
    const { data: tx, error: transError } = await supabase
      .from('wallet_transactions')
      .select('points_earned, transaction_type, wallet_id')
      .in('wallet_id', walletIds);
    if (!transError && tx) transactions = tx;
  }

  const totalMembers = wallets?.length ?? 0;
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const activeMembers =
    wallets?.filter(
      (w) => w.last_transaction_at && new Date(w.last_transaction_at) >= thirtyDaysAgo
    ).length ?? 0;

  const totalPointsIssued =
    transactions.filter((t) => (t.points_earned ?? 0) > 0).reduce((sum, t) => sum + (t.points_earned ?? 0), 0) ?? 0;
  const totalPointsRedeemed =
    transactions.filter((t) => t.transaction_type === 'redemption').reduce((sum, t) => sum + Math.abs(t.points_earned ?? 0), 0) ?? 0;

  const currentTotalPoints = wallets?.reduce((sum, w) => sum + (w.loyalty_points ?? 0), 0) ?? 0;
  const averagePointsPerCustomer = totalMembers > 0 ? currentTotalPoints / totalMembers : 0;

  const membersByTier = {
    basic: wallets?.filter((w) => w.tier === 'basic').length ?? 0,
    premium: wallets?.filter((w) => w.tier === 'premium').length ?? 0,
    bronze: wallets?.filter((w) => w.tier === 'bronze').length ?? 0,
    silver: wallets?.filter((w) => w.tier === 'silver').length ?? 0,
    gold: wallets?.filter((w) => w.tier === 'gold').length ?? 0,
    platinum: wallets?.filter((w) => w.tier === 'platinum').length ?? 0,
  };

  const topTier =
    (Object.entries(membersByTier).sort(([, a], [, b]) => b - a)[0]?.[0] as string) ?? 'basic';

  const totalRewardsRedeemed =
    transactions.filter((t) => t.transaction_type === 'redemption').length ?? 0;

  return {
    totalMembers,
    activeMembers,
    totalPointsIssued,
    totalPointsRedeemed,
    totalRewardsRedeemed,
    averagePointsPerCustomer,
    topTier,
    membersByTier,
  };
}

export async function prefetchLoyaltyDashboardStats(tenantId: string): Promise<void> {
  try {
    const payload = await fetchLoyaltyDashboardData(tenantId);
    setStatsPageCache(loyaltyDashboardCacheKey(tenantId), payload);
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Statistics (solo prefetch con rango por defecto: hoy vs ayer) ---

interface StatisticsPayload {
  todaySales: number;
  yesterdaySales: number;
  totalWithCancellations: number;
  paymentStats: {
    cash: { today: number; yesterday: number };
    card: { today: number; yesterday: number };
    digital: { today: number; yesterday: number };
    transactions: { today: number; yesterday: number };
    averageTicket: { today: number; yesterday: number };
  };
  chartData: Array<{ date?: string; day?: string; hour?: string; [key: string]: string | number | undefined }>;
}

/** Parámetros por defecto para prefetch de Estadísticas (hoy). */
function getDefaultStatisticsParams(): { start: Date; end: Date; channel: string } {
  const today = startOfDay(new Date());
  return { start: today, end: today, channel: 'all' };
}

export async function prefetchStatisticsStats(tenantId: string): Promise<void> {
  const { start, end, channel } = getDefaultStatisticsParams();
  const startStr = format(start, 'yyyy-MM-dd');
  const endStr = format(end, 'yyyy-MM-dd');
  const key = statisticsCacheKey(tenantId, startStr, endStr, channel);
  try {
    const yesterday = subDays(start, 1);
    const [startDate, endDate] = [startOfDay(start), endOfDay(end)];
    const [compStart, compEnd] = [startOfDay(yesterday), endOfDay(yesterday)];

    // Dominio único: orders_v2 (todos los canales). Aliasing grand_total→total,
    // lifecycle_stage→status; payment_method vive en source_metadata.
    const v2Select = 'total:grand_total, status:lifecycle_stage, source_metadata, created_at';
    let rangeQuery = supabase
      .from('orders_v2')
      .select(v2Select)
      .eq('tenant_id', tenantId)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());
    if (channel === 'pos') rangeQuery = rangeQuery.eq('channel', 'pos');
    else if (channel === 'ecommerce') rangeQuery = rangeQuery.eq('channel', 'online_checkout');

    let compQuery = supabase
      .from('orders_v2')
      .select(v2Select)
      .eq('tenant_id', tenantId)
      .gte('created_at', compStart.toISOString())
      .lte('created_at', compEnd.toISOString());
    if (channel === 'pos') compQuery = compQuery.eq('channel', 'pos');
    else if (channel === 'ecommerce') compQuery = compQuery.eq('channel', 'online_checkout');

    const [{ data: rangeData }, { data: comparisonData }] = await Promise.all([
      rangeQuery,
      compQuery,
    ]);

    const rangeOrders = (rangeData ?? []).filter((o: { status?: string }) => o.status !== 'cancelled');
    const comparisonOrders = (comparisonData ?? []).filter((o: { status?: string }) => o.status !== 'cancelled');

    const todaySales = rangeOrders.reduce((s: number, o: { total?: unknown }) => s + Number(o.total ?? 0), 0);
    const yesterdaySales = comparisonOrders.reduce((s: number, o: { total?: unknown }) => s + Number(o.total ?? 0), 0);
    const totalWithCancellations = (rangeData ?? []).reduce((s: number, o: { total?: unknown }) => s + Number(o.total ?? 0), 0);

    const paymentStats = {
      cash: { today: 0, yesterday: 0 },
      card: { today: 0, yesterday: 0 },
      digital: { today: 0, yesterday: 0 },
      transactions: { today: rangeOrders.length, yesterday: comparisonOrders.length },
      averageTicket: {
        today: rangeOrders.length > 0 ? todaySales / rangeOrders.length : 0,
        yesterday: comparisonOrders.length > 0 ? yesterdaySales / comparisonOrders.length : 0,
      },
    };
    const pmOf = (o: { source_metadata?: Record<string, unknown> | null }) =>
      ((o.source_metadata?.payment_method as string) || 'cash');
    rangeOrders.forEach((order: { total?: unknown; source_metadata?: Record<string, unknown> | null }) => {
      const total = Number(order.total ?? 0);
      const m = pmOf(order);
      if (m === 'cash') paymentStats.cash.today += total;
      else if (m === 'card') paymentStats.card.today += total;
      else paymentStats.digital.today += total;
    });
    comparisonOrders.forEach((order: { total?: unknown; source_metadata?: Record<string, unknown> | null }) => {
      const total = Number(order.total ?? 0);
      const m = pmOf(order);
      if (m === 'cash') paymentStats.cash.yesterday += total;
      else if (m === 'card') paymentStats.card.yesterday += total;
      else paymentStats.digital.yesterday += total;
    });

    const chartData: StatisticsPayload['chartData'] = [];
    setStatsPageCache(key, {
      todaySales,
      yesterdaySales,
      totalWithCancellations,
      paymentStats,
      chartData,
    });
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Clientes (lista + KPIs por rango de fechas) ---

export interface CustomersCachePayload {
  ordersList: Array<{
    id: string;
    customer_name: string | null;
    customer_phone: string | null;
    customer_email: string | null;
    total: number;
    created_at: string;
    order_type?: string | null;
  }>;
}

/** Parámetros por defecto para prefetch Clientes (últimos 30 días). */
function getDefaultCustomersParams(): { start: Date; end: Date } {
  const today = endOfDay(new Date());
  const start = startOfDay(subDays(today, 29));
  return { start, end: today };
}

export async function fetchCustomersData(
  tenantId: string,
  branchId: string | null,
  startDate: Date,
  endDate: Date
): Promise<CustomersCachePayload> {
  let query = supabase
    .from('orders_v2')
    .select('id, customer_name, customer_phone, customer_email, total:grand_total, created_at, order_type:channel')
    .eq('tenant_id', tenantId)
    .not('customer_name', 'is', null)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())
    .order('created_at', { ascending: false })
    .limit(10000);
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw error;
  const ordersList = (data ?? []) as CustomersCachePayload['ordersList'];
  return { ordersList };
}

export async function prefetchCustomersStats(tenantId: string, branchId?: string | null): Promise<void> {
  try {
    const { start, end } = getDefaultCustomersParams();
    const payload = await fetchCustomersData(tenantId, branchId ?? null, start, end);
    const key = customersCacheKey(tenantId, branchId ?? null, format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
    setStatsPageCache(key, payload);
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Productos (lista + inventario + stats) ---

export interface ProductsCachePayload {
  products: Array<{
    id: string;
    name: string;
    description: string;
    price: number;
    category: string;
    image_url: string;
    stock: number;
    is_active: boolean;
    show_in_store?: boolean;
    tenant_id?: string;
  }>;
  inventoryTotalByProductId: Record<string, number>;
  stats: { total: number; active: number; draft: number; archived: number };
}

export async function fetchProductsData(tenantId: string): Promise<ProductsCachePayload> {
  const branchesPromise = supabase
    .from('branches')
    .select('id')
    .eq('tenant_id', tenantId)
    .eq('is_active', true);
  const query = supabase
    .from('products')
    .select('id,name,description,price,category,image_url,stock,is_active,show_in_store,tenant_id')
    .eq('tenant_id', tenantId)
    .limit(10000);
  const [productsResult, branchesResult] = await Promise.all([query, branchesPromise]);
  const { data: productsData, error: productsError } = productsResult;
  if (productsError) throw productsError;
  const products = (productsData ?? []) as ProductsCachePayload['products'];
  const productIds = products.map((p) => p.id);
  const branchIds = (branchesResult.data ?? []).map((b: { id: string }) => b.id);

  const inventoryTotalByProductId: Record<string, number> = {};
  if (productIds.length > 0 && branchIds.length > 0) {
    const { data: invRows } = await supabase
      .from('inventory')
      .select('product_id, quantity')
      .in('product_id', productIds)
      .in('branch_id', branchIds);
    (invRows || []).forEach((r: { product_id: string; quantity: number }) => {
      inventoryTotalByProductId[r.product_id] =
        (inventoryTotalByProductId[r.product_id] || 0) + (Number(r.quantity) || 0);
    });
  }

  const [totalRes, activeRes, draftRes, archivedRes] = await Promise.all([
    supabase.from('products').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
    supabase.from('products').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId).eq('is_active', true),
    supabase.from('products').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId).eq('is_active', false).eq('show_in_store', false),
    supabase.from('products').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId).eq('is_active', false).eq('show_in_store', true),
  ]);
  const stats = {
    total: totalRes.count ?? 0,
    active: activeRes.count ?? 0,
    draft: draftRes.count ?? 0,
    archived: archivedRes.count ?? 0,
  };

  return { products, inventoryTotalByProductId, stats };
}

export async function prefetchProductsStats(tenantId: string): Promise<void> {
  try {
    const payload = await fetchProductsData(tenantId);
    setStatsPageCache(productsCacheKey(tenantId), payload);
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Existencias (Inventario): vista "Todos" para cache hit al volver con selectedBranchIds=[] ---

interface ExistenciasCachePayload {
  items: Array<{
    id: string;
    name: string;
    category: string | null;
    image_url: string | null;
    stock: number;
    slug: string | null;
  }>;
}

const branchesCacheByTenant = new Map<string, Array<{ id: string; name: string }>>();

export function getCachedBranches(tenantId: string): Array<{ id: string; name: string }> | null {
  return branchesCacheByTenant.get(tenantId) ?? null;
}

export function setCachedBranches(tenantId: string, branches: Array<{ id: string; name: string }>): void {
  branchesCacheByTenant.set(tenantId, branches);
}

/** Prefetch: productos + inventario agregado (sin filtrar por rama). Clave tenantId| para que al volver con selectedBranchIds=[] haya cache hit. */
export async function fetchExistenciasData(tenantId: string): Promise<ExistenciasCachePayload> {
  const { data: productsData, error: productsError } = await supabase
    .from('products')
    .select('id, name, category, image_url, stock, slug')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('category')
    .order('name');

  if (productsError) throw productsError;
  const productList = productsData ?? [];
  const productIds = productList.map((p) => p.id);

  const byProductId: Record<string, number> = {};
  if (productIds.length > 0) {
    const { data: invData, error: invError } = await supabase
      .from('inventory')
      .select('product_id, quantity')
      .in('product_id', productIds);
    if (!invError && invData) {
      (invData as { product_id: string; quantity: number }[]).forEach((row) => {
        byProductId[row.product_id] = (byProductId[row.product_id] ?? 0) + Number(row.quantity ?? 0);
      });
    }
  }

  const items = productList.map((p) => ({
    id: p.id,
    name: p.name,
    category: (p as { category?: string }).category ?? null,
    image_url: (p as { image_url?: string }).image_url ?? null,
    stock: byProductId[p.id] ?? (p as { stock?: number }).stock ?? 0,
    slug: (p as { slug?: string }).slug ?? null,
  }));

  return { items };
}

export async function prefetchExistenciasStats(tenantId: string): Promise<void> {
  try {
    const [branchesRes, payload] = await Promise.all([
      supabase
        .from('branches')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name'),
      fetchExistenciasData(tenantId),
    ]);
    if (!branchesRes.error && branchesRes.data?.length) {
      setCachedBranches(tenantId, branchesRes.data as Array<{ id: string; name: string }>);
    }
    const cacheKey = listViewCacheKey('existencias', `${tenantId}|`);
    setStatsPageCache(cacheKey, payload);
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Pedidos online (Ecommerce) — rango por defecto: hoy ---

export async function prefetchEcommerceOrdersStats(tenantId: string): Promise<void> {
  try {
    const today = new Date();
    const startDate = startOfDay(today);
    const endDate = endOfDay(today);
    const cacheKey = listViewCacheKey(
      'ecommerce-orders',
      `${tenantId}|${startDate.toISOString()}|${endDate.toISOString()}`
    );
    // Dominio único: orders_v2 (canales online). Aliasing + order_lines; order_number ya viene
    // formateado (E-/S-); payment_method y delivery viven en source_metadata.
    const { data, error } = await supabase
      .from('orders_v2')
      .select(
        `
        id,
        order_number,
        created_at,
        customer_name,
        customer_email,
        customer_phone,
        total:grand_total,
        payment_status,
        status:lifecycle_stage,
        source_metadata,
        order_lines (id, product_name:name, quantity:qty)
      `,
        { count: 'exact' }
      )
      .eq('tenant_id', tenantId)
      .in('channel', ['online_checkout', 'cod_form'])
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(5000);
    if (error) return;
    const ordersData = (data || []).map((order: Record<string, unknown>) => {
      const sm = (order.source_metadata as Record<string, unknown> | null) ?? {};
      return {
        id: order.id,
        order_number: (order.order_number as string) || (order.id as string).slice(0, 8).toUpperCase(),
        created_at: order.created_at,
        customer_name: order.customer_name || 'Sin cliente',
        customer_email: order.customer_email ?? null,
        customer_phone: order.customer_phone ?? null,
        total: Number(order.total ?? 0),
        payment_method: (sm.payment_method as string) || 'cod',
        payment_status: order.payment_status || 'unpaid',
        status: order.status || 'quotation',
        delivery_method: (sm.delivery_method as string) ?? null,
        delivery_address: (sm.delivery_address as string) ?? null,
        items_count: (order.order_lines as unknown[])?.length ?? 0,
      };
    });
    setStatsPageCache(cacheKey, { items: ordersData });
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Ventas (Sales) — rango por defecto: hoy, sin filtrar por sucursal ---

export async function prefetchSalesOrdersStats(tenantId: string, branchId?: string | null): Promise<void> {
  try {
    const today = new Date();
    const startDate = startOfDay(today);
    const endDate = endOfDay(today);
    const cacheKey = listViewCacheKey(
      'sales-orders',
      `${tenantId}|${branchId ?? ''}|${startDate.toISOString()}|${endDate.toISOString()}`
    );
    // Dominio único: orders_v2 (todos los canales). Aliasing + order_lines; order_number ya
    // viene formateado (E-/S-); payment_method vive en source_metadata.
    let query = supabase
      .from('orders_v2')
      .select(
        `
        id,
        order_number,
        created_at,
        customer_name,
        customer_phone,
        total:grand_total,
        payment_status,
        status:lifecycle_stage,
        order_type:channel,
        source_metadata,
        order_lines (id, product_name:name, quantity:qty)
      `
      )
      .eq('tenant_id', tenantId)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .limit(5000);
    if (branchId) query = query.eq('branch_id', branchId);
    const { data, error } = await query;
    if (error) return;
    const salesData = (data || []).map((order: Record<string, unknown>) => {
      const sm = (order.source_metadata as Record<string, unknown> | null) ?? {};
      return {
        id: order.id,
        order_number: (order.order_number as string) ?? (order.id as string).slice(0, 8).toUpperCase(),
        receipt_number: (order.order_number as string) || undefined,
        created_at: order.created_at,
        customer_name: (order.customer_name as string) || undefined,
        customer_phone: (order.customer_phone as string) ?? null,
        total: Number(order.total || 0),
        payment_method: (sm.payment_method as string) || 'cash',
        payment_status: (order.payment_status as string) || 'unpaid',
        status: (order.status as string) || 'quotation',
        order_type: (order.order_type as string) || 'pos',
        items_count: (order.order_lines as unknown[])?.length || 0,
      };
    });
    setStatsPageCache(cacheKey, { items: salesData });
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Carritos abandonados (Ecommerce) — rango por defecto: últimos 30 días ---

export async function prefetchAbandonedCartsStats(tenantId: string): Promise<void> {
  try {
    const today = endOfDay(new Date());
    const start = startOfDay(subDays(today, 29));
    const cacheKey = listViewCacheKey(
      'ecommerce-abandoned-carts',
      `${tenantId}|${start.toISOString()}|${today.toISOString()}`
    );

    const { data, error } = await supabase
      .from('abandoned_carts')
      .select('id, created_at, customer_name, customer_email, customer_phone, total, items, status')
      .eq('tenant_id', tenantId)
      .eq('status', 'abandoned')
      .gte('created_at', start.toISOString())
      .lte('created_at', today.toISOString())
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) return;
    setStatsPageCache(cacheKey, { items: data ?? [] });
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Agencias Shalom (lista global, misma clave que ShalomAgenciesPage) ---

export async function prefetchShalomAgenciesStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('shalom-agencies', tenantId);
    const { data, error } = await supabase
      .from('shalom_agencies')
      .select('id, name, address, distrito, province, department, phone, open_hours, open_hours_sunday, is_active')
      .order('department')
      .order('province')
      .order('name');
    if (error) return;
    const rows = (data ?? []) as Array<{
      id: string;
      name: string | null;
      address: string | null;
      distrito: string | null;
      province: string;
      department: string;
      phone: string | null;
      open_hours: string | null;
      open_hours_sunday: string | null;
      is_active: boolean | null;
    }>;
    const list = rows.map((r) => ({
      id: r.id,
      name: toTitleCase(r.name ?? ''),
      department: toTitleCase(r.department ?? ''),
      province: toTitleCase(r.province ?? ''),
      distrito: r.distrito != null && r.distrito !== '' ? toTitleCase(r.distrito) : null,
      address: r.address != null && r.address !== '' ? toTitleCase(r.address) : null,
      phone: r.phone ?? null,
      open_hours_display: [r.open_hours, r.open_hours_sunday].filter(Boolean).join(' · ') || null,
      is_active: r.is_active !== false,
    }));
    setStatsPageCache(cacheKey, { items: list });
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Productos: categorías, modificadores, etiquetas, variantes (misma clave que cada página) ---

export async function prefetchCategoriesStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('categories', tenantId);
    const [categoriesResult, productsResult] = await Promise.all([
      supabase
        .from('categories')
        .select('id,name,description,image_url,display_order,is_active,tenant_id,slug')
        .eq('tenant_id', tenantId)
        .order('display_order'),
      supabase.from('products').select('category').eq('tenant_id', tenantId),
    ]);
    if (categoriesResult.error || productsResult.error) return;
    const productCounts = (productsResult.data || []).reduce(
      (acc, product: { category?: string | null }) => {
        if (product.category) acc[product.category] = (acc[product.category] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
    const categoriesWithCounts = (categoriesResult.data || []).map(
      (category: { slug?: string; name: string; [k: string]: unknown }) => ({
        ...category,
        icon: '',
        product_count: productCounts[category.name] || 0,
        slug: category.slug ?? slugifyProductName(category.name),
      })
    );
    setStatsPageCache(cacheKey, { items: categoriesWithCounts });
  } catch {
    // Prefetch en segundo plano
  }
}

type ModifierPrefetchRow = {
  id: string;
  slug: string | null;
  name: string;
  price_adjustment: number | null;
  created_at: string;
};

export async function prefetchModifiersStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('modifiers', tenantId);
    const { data: productsData } = await supabase.from('products').select('id').eq('tenant_id', tenantId);
    if (!productsData?.length) {
      setStatsPageCache(cacheKey, { items: [] });
      return;
    }
    const { data, error } = await supabase
      .from('product_modifiers')
      .select('id, slug, name, price_adjustment, created_at')
      .in('product_id', productsData.map((p) => p.id))
      .order('name');
    if (error) return;
    const unique = new Map<string, ModifierPrefetchRow>();
    (data ?? []).forEach((row: ModifierPrefetchRow) => {
      if (unique.has(row.name)) return;
      unique.set(row.name, row);
    });
    const list = Array.from(unique.values()).map((row) => ({
      id: row.id,
      slug: row.slug ?? '',
      name: row.name,
      price_adjustment: row.price_adjustment,
      tenant_id: tenantId,
      created_at: row.created_at,
      is_active: true,
    }));
    setStatsPageCache(cacheKey, { items: list });
  } catch {
    // Prefetch en segundo plano
  }
}

export async function prefetchTagsStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('tags', tenantId);
    const [tagsResult, productsResult] = await Promise.all([
      supabase
        .from('product_tags')
        .select('id, name, tenant_id, created_at')
        .eq('tenant_id', tenantId)
        .order('name'),
      supabase.from('products').select('tags').eq('tenant_id', tenantId),
    ]);
    if (tagsResult.error || productsResult.error) return;
    const productCountByTagName: Record<string, number> = {};
    (productsResult.data || []).forEach((product: { tags?: string[] | string | null }) => {
      let tagNames: string[] = [];
      if (Array.isArray(product.tags)) tagNames = product.tags;
      else if (typeof product.tags === 'string') {
        try {
          const parsed = JSON.parse(product.tags);
          tagNames = Array.isArray(parsed) ? parsed : [];
        } catch {
          tagNames = [];
        }
      }
      tagNames.forEach((tagName: string) => {
        if (tagName) productCountByTagName[tagName] = (productCountByTagName[tagName] || 0) + 1;
      });
    });
    const tagsWithCounts = (tagsResult.data || []).map((tag: { name: string; [k: string]: unknown }) => ({
      ...tag,
      product_count: productCountByTagName[tag.name] || 0,
    }));
    setStatsPageCache(cacheKey, { items: tagsWithCounts });
  } catch {
    // Prefetch en segundo plano
  }
}

type VariantTemplateValuePrefetch = { id: string; value: string };

export async function prefetchVariantsStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('variants', tenantId);
    const { data: templatesData, error: errTemplates } = await supabase
      .from('variant_templates')
      .select('id, name')
      .eq('tenant_id', tenantId)
      .order('name');
    if (errTemplates) return;
    const { data: valuesData } = await supabase
      .from('variant_template_values')
      .select('id, variant_template_id, value, sort_order')
      .order('sort_order');
    const { data: countsData } = await supabase
      .from('product_variants')
      .select('variant_template_id')
      .not('variant_template_id', 'is', null);
    const countByTemplate: Record<string, number> = {};
    (countsData || []).forEach((row: { variant_template_id: string }) => {
      const id = row.variant_template_id;
      countByTemplate[id] = (countByTemplate[id] || 0) + 1;
    });
    const valuesByTemplate: Record<string, VariantTemplateValuePrefetch[]> = {};
    (valuesData || []).forEach((row: { id: string; variant_template_id: string; value: string }) => {
      const tid = row.variant_template_id;
      if (!valuesByTemplate[tid]) valuesByTemplate[tid] = [];
      valuesByTemplate[tid].push({ id: row.id, value: row.value });
    });
    const list = (templatesData || []).map((t: { id: string; name: string }) => ({
      id: t.id,
      name: t.name,
      values: valuesByTemplate[t.id] || [],
      product_count: countByTemplate[t.id] ?? 0,
    }));
    setStatsPageCache(cacheKey, { items: list });
  } catch {
    // Prefetch en segundo plano
  }
}

// --- Ecommerce / Ajustes: métodos de pago, entrega, reglas, páginas, dominios, couriers ---

export async function prefetchEcommercePaymentMethodsStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('ecommerce-payment-methods', tenantId);
    const { data, error } = await supabase
      .from('ecommerce_payment_methods')
      .select('id, name, description, icon_url, enabled, display_order, created_at, updated_at, is_default')
      .eq('tenant_id', tenantId)
      .order('display_order', { ascending: true });
    if (error) return;
    setStatsPageCache(cacheKey, { items: data ?? [] });
  } catch {
    // Prefetch en segundo plano
  }
}

export async function prefetchEcommerceDeliveryMethodsStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('ecommerce-delivery-methods', tenantId);
    try {
      await initializeDeliveryMethods(tenantId);
    } catch {
      // igual que la página: no bloquear prefetch
    }
    const { data, error } = await supabase
      .from('ecommerce_delivery_methods')
      .select('id, name, description, icon_url, enabled, display_order, created_at, updated_at')
      .eq('tenant_id', tenantId)
      .order('display_order', { ascending: true });
    if (error) return;
    setStatsPageCache(cacheKey, { items: data ?? [] });
  } catch {
    // Prefetch en segundo plano
  }
}

export async function prefetchEcommerceDeliveryRulesStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('ecommerce-delivery-rules', tenantId);
    const [rulesRes, zonesRes] = await Promise.all([
      supabase
        .from('ecommerce_delivery_rules')
        .select(
          'id, name, description, rule_type, restaurant_amount, restaurant_percentage, start_date, end_date, days_of_week, start_time, end_time, applies_to_all_zones, zone_ids, is_active, priority, created_at, updated_at'
        )
        .eq('tenant_id', tenantId)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false }),
      supabase
        .from('ecommerce_delivery_districts')
        .select('id, district_name, zone_name, delivery_fee')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('district_name', { ascending: true }),
    ]);
    if (rulesRes.error || zonesRes.error) return;
    setStatsPageCache(cacheKey, {
      rules: rulesRes.data ?? [],
      zones: zonesRes.data ?? [],
    });
  } catch {
    // Prefetch en segundo plano
  }
}

export async function prefetchEcommercePagesStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('ecommerce-pages', tenantId);
    const { data, error } = await supabase
      .from('content_pages')
      .select('id, title, slug, is_published, created_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    if (error) {
      const err = error as { code?: string; message?: string };
      const isTableMissing =
        err?.code === 'PGRST205' ||
        (typeof err?.message === 'string' &&
          err.message.includes('content_pages') &&
          err.message.includes('schema cache'));
      if (isTableMissing) setStatsPageCache(cacheKey, { items: [], tableMissing: true });
      return;
    }
    setStatsPageCache(cacheKey, { items: data ?? [], tableMissing: false });
  } catch {
    // Prefetch en segundo plano
  }
}

export async function prefetchTenantDomainsStats(tenantId: string): Promise<void> {
  try {
    const cacheKey = listViewCacheKey('tenant-domains', tenantId);
    const [tenantRes, domainsRes] = await Promise.all([
      supabase.from('tenants').select('slug').eq('id', tenantId).single(),
      supabase
        .from('tenant_domains')
        .select(
          'id, tenant_id, domain, subdomain, is_custom, is_active, is_verified, domain_type, dns_record_type, dns_record_value, verification_status, verification_checked_at, notes, created_at, updated_at'
        )
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false }),
    ]);
    if (tenantRes.error && !domainsRes.error) {
      // slug opcional
    }
    if (domainsRes.error) {
      const e = domainsRes.error as { code?: string; message?: string };
      if (e.code === '42P01' || e.message?.includes('does not exist')) {
        setStatsPageCache(cacheKey, { domains: [], defaultSubdomain: tenantRes.data?.slug ?? null });
      }
      return;
    }
    setStatsPageCache(cacheKey, {
      domains: domainsRes.data ?? [],
      defaultSubdomain: tenantRes.data?.slug ?? null,
    });
  } catch {
    // Prefetch en segundo plano
  }
}

export async function prefetchLogisticsCouriersCoverageStats(tenantId: string): Promise<void> {
  try {
    const modeKey = 'all';
    const cacheKey = listViewCacheKey('logistics-couriers-coverage', `${tenantId}|${modeKey}`);
    const query = supabase
      .from('logistics_couriers')
      .select(
        `
        id, name, type, has_api, is_system,
        logistics_courier_services ( id, name, delivery_type ),
        logistics_courier_coverage ( id, department, province, district, coverage_type )
      `
      )
      .order('name');
    const { data, error } = await query;
    if (error) return;
    const couriers = data ?? [];
    const { data: settingsRows } = await supabase
      .from('logistics_courier_settings')
      .select('courier_id, enabled')
      .eq('tenant_id', tenantId);
    const settings: Record<string, { enabled: boolean }> = {};
    (settingsRows ?? []).forEach((r: { courier_id: string; enabled: boolean }) => {
      settings[r.courier_id] = { enabled: r.enabled };
    });
    setStatsPageCache(cacheKey, { couriers, settings });
  } catch {
    // Prefetch en segundo plano
  }
}
